import { Pencil } from 'lucide-react';
import { forwardRef } from 'react';

import styles from './KaiCoachEditControls.module.css';

interface EditCoachButtonProps {
  onClick: () => void;
}

/**
 * The floating "Edit Coach" button in the bottom-right corner, the coach's
 * counterpart of a chapter's "Edit Content". The ref lets the page return
 * focus here when edit mode ends.
 */
export const EditCoachButton = forwardRef<HTMLButtonElement, EditCoachButtonProps>(
  function EditCoachButton({ onClick }, ref) {
    return (
      <div className={styles.dock}>
        <button
          aria-label="Edit coach"
          className={styles.editButton}
          onClick={onClick}
          ref={ref}
          type="button"
        >
          <span className={styles.fab}>
            <Pencil aria-hidden="true" size={22} strokeWidth={2.4} />
          </span>
          <span aria-hidden="true" className={styles.fabLabel}>
            Edit Coach
          </span>
        </button>
      </div>
    );
  },
);

interface CoachEditBarProps {
  isDirty: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onDone: () => void;
  onSave: () => void;
  saveFailed: boolean;
}

/**
 * The floating bar shown while editing a coach: Cancel (unsaved changes) or
 * Done (nothing to save), Save, and a status line that says which.
 */
export function CoachEditBar({
  isDirty,
  isSaving,
  onCancel,
  onDone,
  onSave,
  saveFailed,
}: CoachEditBarProps) {
  const status = isSaving
    ? 'Saving changes…'
    : saveFailed && isDirty
      ? 'Could not save. Please try again.'
      : isDirty
        ? 'Unsaved changes'
        : 'All changes saved';
  const tone = isSaving ? 'saving' : saveFailed && isDirty ? 'error' : isDirty ? 'dirty' : 'saved';
  const saveDisabled = !isDirty || isSaving;

  return (
    <div className={styles.dock}>
      <div aria-label="Coach edit actions" className={styles.bar} role="region">
        <button
          className={styles.secondaryButton}
          onClick={isDirty ? onCancel : onDone}
          type="button"
        >
          {isDirty ? 'Cancel' : 'Done'}
        </button>
        <div className={styles.saveGroup}>
          {/* data-variant marks it as a primary action, like the shared
              Button's, so app-wide checks of that fill see it too. */}
          <button
            className={styles.saveButton}
            data-variant="primary"
            disabled={saveDisabled}
            onClick={onSave}
            type="button"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <p aria-atomic="true" aria-live="polite" className={styles.status} data-tone={tone}>
            {status}
          </p>
        </div>
      </div>
    </div>
  );
}
